import type {
  ContextOperatorKey,
  ContextPredicateType
} from '@kentik/ui-shared/alertingManager/contextFilterCapabilities';

const FIELD_LABELS: Record<string, string> = {
  device_id: 'Device',
  device_name: 'Device Name',
  device_labels: 'Device Labels',
  site_id: 'Site',
  site_name: 'Site Name',
  interface_id: 'Interface',
  interface_index: 'Interface Index',
  interface_name: 'Interface Name',
  interface_description: 'Interface Description',
  ip_address: 'IP Address',
  mac_address: 'MAC Address'
};

const SCOPE_LABELS: Record<string, string> = {
  NETWORK_SCOPE_ANY: 'Unscoped',
  NETWORK_SCOPE_ROOT: 'Non-directional',
  NETWORK_SCOPE_SOURCE: 'Source',
  NETWORK_SCOPE_DESTINATION: 'Destination',
  NETWORK_SCOPE_ADJACENT: 'Adjacent'
};

interface OperatorLabel {
  positive: string;
  negative: string;
}

const OPERATOR_LABELS: Record<ContextOperatorKey, OperatorLabel> = {
  equals: { positive: 'Equals', negative: 'Does not equal' },
  in: { positive: 'Is one of', negative: 'Is not one of' },
  contains: { positive: 'Contains', negative: 'Does not contain' },
  starts_with: { positive: 'Starts with', negative: 'Does not start with' },
  ends_with: { positive: 'Ends with', negative: 'Does not end with' },
  matches_regex: { positive: 'Matches regex', negative: 'Does not match regex' },
  contains_all: { positive: 'Has all of', negative: 'Does not have all of' },
  within: { positive: 'Is within', negative: 'Is not within' },
  within_any: { positive: 'Is within any of', negative: 'Is not within any of' }
};

const OPERATOR_LABEL_OVERRIDES: Partial<
  Record<ContextPredicateType, Partial<Record<ContextOperatorKey, OperatorLabel>>>
> = {
  labels: {
    equals: { positive: 'Has label', negative: 'Does not have label' },
    in: { positive: 'Has any of', negative: 'Has none of' }
  }
};

const humanizeKey = (key: string): string =>
  key
    .split(/[_.]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

export const getContextFieldLabel = (fieldKey: string): string => FIELD_LABELS[fieldKey] ?? humanizeKey(fieldKey);

export const getContextScopeLabel = (scope: string): string => SCOPE_LABELS[scope] ?? humanizeKey(scope);

export function getContextOperatorLabel(
  operatorKey: string,
  { predicateType, not = false }: { predicateType?: ContextPredicateType; not?: boolean } = {}
): string {
  const override = predicateType
    ? OPERATOR_LABEL_OVERRIDES[predicateType]?.[operatorKey as ContextOperatorKey]
    : undefined;
  const label = override ?? OPERATOR_LABELS[operatorKey as ContextOperatorKey];

  if (!label) {
    const humanized = humanizeKey(operatorKey).toLowerCase();
    return not ? `Does not ${humanized}` : humanizeKey(operatorKey);
  }

  return not ? label.negative : label.positive;
}
